/**
 * Hardcoded assignee options for the loan-drilldown role pickers (Assigned
 * Team panel). Keyed by the loan field each role writes to. Values are the
 * user's email (what gets saved); labels are the display name.
 *
 * Dropdowns render each item as `{label} <{value}>`, e.g.
 * "Liam Farrell <liam@eastviewinv.com>".
 *
 * NOTE: these are a stopgap until the backend serves role rosters; roles not
 * listed here (e.g. salesAnalyst, loanCloser) fall back to the general
 * user-email selector.
 *
 * ⚠️ EASTVIEW-ONLY. This roster — and every Assigned Team / Assigned Users
 * change built on it (the underwriter list, the Pre-UW "Junior Underwriter"
 * team, QC, etc.) — is specific to Eastview/Arrowpointe staffing
 * (`@eastviewinv.com` / `@arrowpointemgmt.com` / `@arrowservicing.com`). Do
 * NOT propagate it to the sibling `lendmarq-los` repo: Lendmarq has a
 * different team and its own roster. If any of these files are ever synced to
 * a sibling, this roster and the role set must be excluded / replaced per that
 * partner. See SHARED_COMPONENTS.md and the CLAUDE.md conventions note.
 */
export const LOAN_ROLE_OPTIONS = {
  structurer: [
    { label: "Liam Farrell", value: "liam@eastviewinv.com" },
    { label: "Shon Katz", value: "skatz@eastviewinv.com" },
  ],
  // Opening Team — loan processors. Assigned to the loan's `processor` field.
  processor: [
    { label: "Anil Kumar", value: "anil@arrowservicing.com" },
    { label: "Bhavani Vallapu", value: "bhavani@arrowservicing.com" },
    { label: "Durga Prasad", value: "durga@arrowservicing.com" },
    { label: "Gpally Nikhil", value: "nikhilg@arrowservicing.com" },
    { label: "Shiva Julooru", value: "shivaj@arrowservicing.com" },
  ],
  accountManager: [
    { label: "Bryce Kopcak", value: "bkopcak@eastviewinv.com" },
    { label: "Daniela Felman", value: "daniela@eastviewinv.com" },
    { label: "Emily Haas", value: "emily@eastviewinv.com" },
    { label: "Kia Kremer", value: "kkremer@arrowpointemgmt.com" },
    { label: "Paul Rocha", value: "procha@eastviewinv.com" },
  ],
  underwriter: [
    {
      label: "Christina Matheny",
      value: "christina.matheny@arrowpointemgmt.com",
    },
    { label: "Emilee Howard", value: "emilee@arrowpointemgmt.com" },
    { label: "Heather Reaves", value: "heather.reaves@arrowpointemgmt.com" },
    { label: "Keith Levy", value: "keith@arrowpointemgmt.com" },
    { label: "Katie McWade", value: "katie.mcwade@arrowpointemgmt.com" },
    { label: "Nelson Yang", value: "nelson.yang@arrowpointemgmt.com" },
    { label: "Sarena Raimondo", value: "sarena@arrowpointemgmt.com" },
    { label: "Tiffany Darcy", value: "tiffany@arrowpointemgmt.com" },
    { label: "Viranda Dixon", value: "viranda@arrowpointemgmt.com" },
  ],
  // Pre-UW Team — reviews files prior to credit underwrite. Assigned to the
  // loan's `preUnderwriter` field, labelled "Junior Underwriter" in the UI.
  preUnderwriter: [
    { label: "Afroz Ahmed", value: "afroz.ahmed@arrowservicing.com" },
    { label: "Gpally Nikhil", value: "nikhilg@arrowservicing.com" },
    {
      label: "Bathula Nithin Teja Goud",
      value: "nithin.goud@arrowpointemgmt.com",
    },
  ],
  // QC Team — post-underwrite quality control. Assigned to the loan's
  // `qc_analyst` field, labelled "QC Review" in the UI.
  qc_analyst: [
    { label: "Pradeep Tammali", value: "ptammali@arrowservicing.com" },
  ],
  closers: [
    {
      label: "Christina Baucom",
      value: "christina.baucom@arrowpointemgmt.com",
    },
    { label: "Marcie Reynolds", value: "marcie@arrowpointemgmt.com" },
    { label: "Pradeep Tammali", value: "ptammali@arrowservicing.com" },
    { label: "Rebecca Rietta", value: "rebecca.rietta@arrowpointemgmt.com" },
    { label: "Shyrose Mansia", value: "shyrose@arrowpointemgmt.com" },
  ],
  admin: [
    { label: "Amy Doshi", value: "amy.doshi@resfinpartners.com" },
    { label: "Christopher Wolpert", value: "dev1@arrowservicing.com" },
    { label: "Danny Shum", value: "danny@arrowservicing.com" },
    { label: "Divyam Vora", value: "dvora@resfinpartners.com" },
    { label: "George Papadeas", value: "george@eastviewinv.com" },
    { label: "Holden Hargrave", value: "holden.hargrave@Lendmarq.com" },
    { label: "Jimmy Oliva", value: "dev2@arrowservicing.com" },
    { label: "Jordan Suppan", value: "jordan@resfinpartners.com" },
    { label: "Kate Magladry", value: "kate@resfinpartners.com" },
    { label: "Larbi Benslimane", value: "larbi@resfinpartners.com" },
    { label: "Pablo Acevedo", value: "pacevedo@resfinpartners.com" },
    { label: "Pablo Escobar", value: "pescobar@resfinpartners.com" },
    { label: "Shyrose Mansia", value: "shyrose@arrowpointemgmt.com" },
  ],
};

/**
 * True when `email` appears in any of the given roster keys
 * (e.g. emailInRoles(user, ["underwriter", "admin"])). Case-insensitive.
 */
export const emailInRoles = (email, roleKeys = []) => {
  if (!email) return false;
  const lc = String(email).toLowerCase();
  return roleKeys.some((key) =>
    (LOAN_ROLE_OPTIONS[key] || []).some(
      (o) => String(o?.value).toLowerCase() === lc,
    ),
  );
};

export default LOAN_ROLE_OPTIONS;
